import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { getSignupDraft, updateSignupDraft } from "@/lib/storage";
import { useAuth } from "@/context/AuthContext";
import {
    AuthScreen,
    AuthBody,
    AuthFooter,
    ScreenHeader,
    TextField,
    PrimaryButton,
} from "@/screens/auth/_ui";
import PasswordStrength from "@/components/PasswordStrength";
import { BUSINESS_CATEGORIES } from "@/lib/businessCategories";

// Age gate — Neyra requires users to be at least 16.
const MIN_AGE = 16;
const ageFromDOB = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const now = new Date();
    let age = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
    return age;
};

const SignupDetails = () => {
    const navigate = useNavigate();
    const { usernameIsTaken } = useAuth();
    const [draft, setDraft] = useState(null);
    const [accountType, setAccountType] = useState("personal");

    // Personal fields
    const [fullName, setFullName] = useState("");
    const [dob, setDob] = useState("");

    // Business fields
    const [businessName, setBusinessName] = useState("");
    const [businessCategory, setBusinessCategory] = useState("");
    const [businessRegNumber, setBusinessRegNumber] = useState("");
    const [businessAddress, setBusinessAddress] = useState("");
    const [businessLocation, setBusinessLocation] = useState("");

    // Shared
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPw, setConfirmPw] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [terms, setTerms] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        const d = getSignupDraft();
        if (!d?.method || (!d.phone && !d.email)) {
            navigate("/auth/signup", { replace: true });
            return;
        }
        setDraft(d);
        if (d.accountType) setAccountType(d.accountType);
    }, [navigate]);

    if (!draft) return null;
    const isBusiness = accountType === "business";

    const validate = () => {
        const e = {};
        const u = username.trim();
        if (!u) e.username = "Choose a username";
        else if (!/^[a-zA-Z0-9_.]{3,20}$/.test(u))
            e.username = "3–20 chars, letters, numbers, _ or .";
        else if (usernameIsTaken(u)) e.username = "Username already taken";

        if (isBusiness) {
            if (!businessName.trim() || businessName.trim().length < 2)
                e.businessName = "Enter your business name";
            if (!businessCategory.trim())
                e.businessCategory = "Pick a business category";
            if (!businessAddress.trim())
                e.businessAddress = "Business address is required";
            if (!businessLocation.trim())
                e.businessLocation = "City / location is required";
        } else {
            if (!fullName.trim() || fullName.trim().length < 2)
                e.fullName = "Enter your full name";
            const age = ageFromDOB(dob);
            if (!dob) e.dob = "Select your date of birth";
            else if (age === null) e.dob = "Invalid date";
            else if (age < MIN_AGE)
                e.dob = `You must be at least ${MIN_AGE} years old to use Neyra.`;
        }

        if (!password) e.password = "Create a password";
        else if (password.length < 6) e.password = "At least 6 characters";
        if (password !== confirmPw) e.confirmPw = "Passwords don't match";
        if (!terms) e.terms = "Accept the terms to continue";
        return e;
    };

    const handleContinue = () => {
        const e = validate();
        if (Object.keys(e).length) {
            setErrors(e);
            return;
        }
        setErrors({});
        updateSignupDraft(
            isBusiness
                ? {
                      accountType: "business",
                      // Business uses business name as the display name.
                      fullName: businessName.trim(),
                      businessName: businessName.trim(),
                      businessCategory: businessCategory.trim(),
                      businessRegNumber: businessRegNumber.trim() || null,
                      businessAddress: businessAddress.trim(),
                      businessLocation: businessLocation.trim(),
                      location: businessLocation.trim(),
                      username: username.trim(),
                      password,
                      acceptedTerms: true,
                  }
                : {
                      accountType: "personal",
                      fullName: fullName.trim(),
                      username: username.trim(),
                      dob,
                      password,
                      acceptedTerms: true,
                  }
        );
        navigate("/auth/signup/contact");
    };

    // Constrain DOB picker so user can't pick a future date or absurd past date
    const today = new Date();
    const maxDob = new Date(today.getFullYear() - MIN_AGE, today.getMonth(), today.getDate())
        .toISOString()
        .slice(0, 10);
    const minDob = "1900-01-01";

    return (
        <AuthScreen testId="signup-details-screen">
            <ScreenHeader title="Your details" />
            <AuthBody>
                <div className="pt-4 space-y-1 animate-fade-up">
                    <h2 className="font-display text-[22px] font-semibold tracking-tight">
                        {isBusiness ? "Tell us about your business" : "Tell us about you"}
                    </h2>
                    <p className="text-[13.5px] text-muted-foreground">
                        {isBusiness
                            ? "Business name and username can be changed once every 30 days."
                            : "Name and username can be changed once every 30 days."}
                    </p>
                </div>

                <div className="mt-6 space-y-4 animate-fade-up" style={{ animationDelay: "80ms" }}>
                    {/* Account type toggle — same row "Change" semantics as Method.jsx */}
                    <div className="flex items-center justify-between" data-testid="signup-account-type">
                        <span className="text-[11.5px] uppercase tracking-[0.14em] text-muted-foreground font-medium">
                            Signing up as · {isBusiness ? "Business" : "Personal"}
                        </span>
                        <button
                            type="button"
                            data-testid="signup-account-type-change"
                            onClick={() =>
                                setAccountType(isBusiness ? "personal" : "business")
                            }
                            className="text-[12px] font-medium text-primary hover:underline"
                        >
                            Change
                        </button>
                    </div>

                    {/* PERSONAL FIELDS */}
                    {!isBusiness && (
                        <>
                            <TextField
                                testId="signup-fullname-input"
                                label="Full name"
                                placeholder="Your full name"
                                value={fullName}
                                onChange={(v) => {
                                    setFullName(v);
                                    if (errors.fullName) setErrors({ ...errors, fullName: null });
                                }}
                                autoComplete="name"
                                error={errors.fullName}
                            />
                            <TextField
                                testId="signup-dob-input"
                                label="Date of birth"
                                placeholder="YYYY-MM-DD"
                                type="date"
                                value={dob}
                                onChange={(v) => {
                                    setDob(v);
                                    if (errors.dob) setErrors({ ...errors, dob: null });
                                }}
                                error={errors.dob}
                            />
                            <style>{`
                                input[type="date"]::-webkit-calendar-picker-indicator { cursor: pointer; filter: invert(var(--cal-invert, 0)); }
                                .dark input[type="date"] { color-scheme: dark; }
                            `}</style>
                        </>
                    )}

                    {/* BUSINESS FIELDS */}
                    {isBusiness && (
                        <>
                            <TextField
                                testId="signup-business-name-input"
                                label="Business name"
                                placeholder="e.g. Glow Studio"
                                value={businessName}
                                onChange={(v) => {
                                    setBusinessName(v);
                                    if (errors.businessName) setErrors({ ...errors, businessName: null });
                                }}
                                error={errors.businessName}
                            />
                            <div data-testid="signup-business-category-field">
                                <span className="block text-[12px] font-medium text-muted-foreground mb-1.5 ml-1">
                                    Business category
                                </span>
                                <select
                                    data-testid="signup-business-category-select"
                                    value={businessCategory}
                                    onChange={(e) => {
                                        setBusinessCategory(e.target.value);
                                        if (errors.businessCategory) setErrors({ ...errors, businessCategory: null });
                                    }}
                                    className={[
                                        "w-full h-14 px-4 rounded-2xl border text-[15px] bg-secondary outline-none transition-colors appearance-none",
                                        errors.businessCategory
                                            ? "border-destructive bg-destructive/5"
                                            : "border-border focus:bg-background focus:border-primary",
                                    ].join(" ")}
                                >
                                    <option value="">Select a category…</option>
                                    {BUSINESS_CATEGORIES.map((c) => (
                                        <option key={c.id} value={c.label}>
                                            {c.label}
                                        </option>
                                    ))}
                                </select>
                                {errors.businessCategory && (
                                    <p data-testid="signup-business-category-error" className="text-[12px] text-destructive mt-1.5 ml-1">
                                        {errors.businessCategory}
                                    </p>
                                )}
                                {businessCategory === "Other (specify)" && (
                                    <input
                                        data-testid="signup-business-category-other"
                                        placeholder="Tell us what you do"
                                        onChange={(e) => setBusinessCategory(e.target.value || "Other (specify)")}
                                        className="mt-2 w-full h-12 px-3 rounded-xl border border-border bg-secondary outline-none text-[14px] focus:bg-background focus:border-primary"
                                    />
                                )}
                            </div>
                            <TextField
                                testId="signup-business-reg-input"
                                label="Registration number (optional)"
                                placeholder="RC123456 / BN123456"
                                value={businessRegNumber}
                                onChange={setBusinessRegNumber}
                            />
                            <TextField
                                testId="signup-business-address-input"
                                label="Business address"
                                placeholder="Street, building, area"
                                value={businessAddress}
                                onChange={(v) => {
                                    setBusinessAddress(v);
                                    if (errors.businessAddress) setErrors({ ...errors, businessAddress: null });
                                }}
                                error={errors.businessAddress}
                            />
                            <TextField
                                testId="signup-business-location-input"
                                label="City / Location"
                                placeholder="e.g. Lagos, Nigeria"
                                value={businessLocation}
                                onChange={(v) => {
                                    setBusinessLocation(v);
                                    if (errors.businessLocation) setErrors({ ...errors, businessLocation: null });
                                }}
                                error={errors.businessLocation}
                            />
                        </>
                    )}

                    <TextField
                        testId="signup-username-input"
                        label={isBusiness ? "Business username" : "Username"}
                        placeholder={isBusiness ? "e.g. glow_studio" : "e.g. ada_b"}
                        value={username}
                        onChange={(v) => {
                            setUsername(v.replace(/\s+/g, ""));
                            if (errors.username) setErrors({ ...errors, username: null });
                        }}
                        autoComplete="off"
                        error={errors.username}
                        maxLength={20}
                    />

                    <div>
                        <TextField
                            testId="signup-password-input"
                            label="Password"
                            placeholder="At least 6 characters"
                            type={showPw ? "text" : "password"}
                            value={password}
                            onChange={(v) => {
                                setPassword(v);
                                if (errors.password) setErrors({ ...errors, password: null });
                            }}
                            autoComplete="new-password"
                            error={errors.password}
                            right={
                                <button
                                    type="button"
                                    data-testid="signup-password-toggle"
                                    onClick={() => setShowPw((s) => !s)}
                                    className="h-9 w-9 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground"
                                >
                                    {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            }
                        />
                        <PasswordStrength value={password} testId="signup-password-strength" />
                    </div>
                    <TextField
                        testId="signup-confirm-password-input"
                        label="Confirm password"
                        placeholder="Re-enter your password"
                        type={showPw ? "text" : "password"}
                        value={confirmPw}
                        onChange={(v) => {
                            setConfirmPw(v);
                            if (errors.confirmPw) setErrors({ ...errors, confirmPw: null });
                        }}
                        error={errors.confirmPw}
                    />

                    <label
                        className="flex items-start gap-3 px-1 pt-1 cursor-pointer select-none"
                        data-testid="signup-terms-label"
                    >
                        <input
                            type="checkbox"
                            data-testid="signup-terms-checkbox"
                            checked={terms}
                            onChange={(e) => {
                                setTerms(e.target.checked);
                                if (errors.terms) setErrors({ ...errors, terms: null });
                            }}
                            className="mt-0.5 h-5 w-5 rounded-md border-border accent-[hsl(var(--primary))]"
                        />
                        <span className="text-[13px] text-muted-foreground leading-snug">
                            I agree to Neyra's{" "}
                            <span className="text-foreground font-medium">Terms</span> and{" "}
                            <span className="text-foreground font-medium">Privacy Policy</span>.
                        </span>
                    </label>
                    {errors.terms && (
                        <p data-testid="signup-terms-error" className="text-[12.5px] text-destructive ml-8">
                            {errors.terms}
                        </p>
                    )}
                </div>

                {/* Attach min/max to the native date input after render */}
                {!isBusiness && <DateBounds min={minDob} max={maxDob} />}
            </AuthBody>

            <AuthFooter>
                <PrimaryButton testId="signup-details-continue" onClick={handleContinue}>
                    Continue
                </PrimaryButton>
            </AuthFooter>
        </AuthScreen>
    );
};

// Tiny helper that applies min/max to the DOB native date input after mount.
const DateBounds = ({ min, max }) => {
    useEffect(() => {
        const el = document.querySelector('[data-testid="signup-dob-input"]');
        if (el) {
            el.min = min;
            el.max = max;
        }
    }, [min, max]);
    return null;
};

export default SignupDetails;
